'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { asHTML, asText, Content, RichTextField } from '@prismicio/client';
import { ImQuotesLeft } from 'react-icons/im';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

// title/feature-title fields are single-paragraph (`single: "strong"`), so
// asHTML always wraps them in one <p> — strip it since these render inside h2/h3.
const headingHtml = (field: RichTextField | undefined) =>
  (asHTML(field) || '').replace(/^<p>|<\/p>$/g, '');

const GOLD = '#bda154';

export interface FeatureQuoteItem {
  title: RichTextField;
  description: RichTextField;
}

export interface FeatureQuoteProps {
  eyelash?: string;
  title?: RichTextField;
  description?: RichTextField;
  features?: FeatureQuoteItem[];
  quotes?: Content.QuotesDocument[];
}

const FeatureQuote = ({
  eyelash,
  title,
  description,
  features = [],
  quotes = [],
}: FeatureQuoteProps) => {
  const [index, setIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);

  const goTo = useCallback(
    (i: number) => {
      if (quotes.length === 0) return;
      setIndex(((i % quotes.length) + quotes.length) % quotes.length);
    },
    [quotes.length]
  );

  useEffect(() => {
    if (quotes.length < 2) return;
    const timer = setInterval(() => goTo(index + 1), 8000);
    return () => clearInterval(timer);
  }, [index, goTo, quotes.length]);

  const onTouchStart = (e: React.TouchEvent) => setTouchStart(e.targetTouches[0].clientX);
  const onTouchEnd = (e: React.TouchEvent) => {
    const distance = touchStart - e.changedTouches[0].clientX;
    if (distance > 50) goTo(index + 1);
    if (distance < -50) goTo(index - 1);
  };

  const quote = quotes[index];
  const bgImage = quote?.data.large_photo?.url ? quote.data.large_photo : quote?.data.wide_image;
  const subline = [quote?.data.position, quote?.data.company].filter(Boolean).join(' · ');

  return (
    <section className="bg-white">
      <div className="grid lg:grid-cols-[1.15fr_1fr]">
        {/* LEFT — contained content */}
        <div className="bg-[#F4F5F7] px-6 py-16 sm:px-8 sm:py-24 lg:py-28 lg:pl-16 lg:pr-12 xl:pl-20 2xl:pl-24">
          {eyelash && (
            <p className="mb-12 text-sm font-semibold uppercase tracking-widest text-blue-600">
              {eyelash}
            </p>
          )}
          <h2
            className="max-w-xl text-3xl font-normal sm:font-light leading-[1.05] text-black [&_strong]:font-medium [&_strong]:text-blue-600 sm:text-4xl md:text-5xl"
            dangerouslySetInnerHTML={{ __html: headingHtml(title) }}
          />
          <div className="mt-6 max-w-xl text-base leading-relaxed text-gray-500 sm:text-lg">
            <PrismicRichText field={description} />
          </div>

          {features.length > 0 && (
            <ol className="mt-16 max-w-xl space-y-8">
              {features.map((feature, i) => (
                <li key={i} className="flex gap-6">
                  <span className="pt-1 sm:pt-2 text-sm font-bold" style={{ color: GOLD }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3
                      className="font-medium text-black text-xl leading-[1.15] mb-1"
                      dangerouslySetInnerHTML={{ __html: headingHtml(feature.title) }}
                    />
                    <div className="mt-1 text-base leading-relaxed text-gray-500 sm:text-base">
                      <PrismicRichText field={feature.description} />
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* RIGHT — full-bleed quote carousel */}
        {quote && (
          <div
            className="relative min-h-[420px] overflow-hidden bg-blue-900 lg:min-h-0"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {bgImage?.url && (
              <PrismicNextImage
                field={bgImage}
                className="absolute inset-0 h-full w-full object-cover"
                fill
                sizes="(min-width: 1024px) 45vw, 100vw"
                fallbackAlt=""
              />
            )}
            <div className="absolute inset-0 bg-blue-900/85" />

            <AnimatePresence mode="wait">
              <motion.div
                key={quote.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="relative z-10 flex h-full flex-col justify-center px-6 py-16 sm:px-8 sm:py-24 lg:py-28 lg:pl-12 lg:pr-16 xl:pr-20 2xl:pr-24"
              >
                <ImQuotesLeft className="mb-4 text-5xl text-blue-300 opacity-60" aria-hidden="true" />
                <blockquote className="sm:font-light max-w-xl text-xl font-normal italic leading-snug text-white sm:text-3xl">
                  <PrismicRichText field={quote.data.full_quote} />
                </blockquote>
                <div className="mt-8 h-0.5 w-10" style={{ backgroundColor: GOLD }} />
                <div
                  className="mt-4 text-xl font-medium [&_strong]:font-semibold [&_strong]:uppercase"
                  style={{ color: GOLD }}
                  dangerouslySetInnerHTML={{ __html: headingHtml(quote.data.name) }}
                />
                {subline && <p className="text-sm text-white/60">{subline}</p>}
              </motion.div>
            </AnimatePresence>

            {quotes.length > 1 && (
              <>
                <button
                  onClick={() => goTo(index - 1)}
                  aria-label="Previous quote"
                  className="absolute left-4 top-1/2 z-20 hidden -translate-y-1/2 rounded-full bg-black/20 p-2 text-white opacity-70 transition-opacity hover:opacity-100 lg:block"
                >
                  <FiChevronLeft size={20} />
                </button>
                <button
                  onClick={() => goTo(index + 1)}
                  aria-label="Next quote"
                  className="absolute right-4 top-1/2 z-20 hidden -translate-y-1/2 rounded-full bg-black/20 p-2 text-white opacity-70 transition-opacity hover:opacity-100 lg:block"
                >
                  <FiChevronRight size={20} />
                </button>
                <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2 lg:bottom-8 lg:left-12 lg:translate-x-0 xl:left-20 2xl:left-24">
                  {quotes.map((q, i) => (
                    <button
                      key={q.id}
                      onClick={() => goTo(i)}
                      aria-label={`Go to quote ${i + 1}${quote ? ` — ${asText(quote.data.name)}` : ''}`}
                      className={`h-2 rounded-full transition-all ${
                        i === index ? 'w-4 bg-white' : 'w-2 bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureQuote;
